import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiPageResponse } from '../types/api.types';
import {
  AdminCreateZoneRequest,
  AdminUpdateZoneRequest,
  CommissionConfig,
  CommissionConfigHistory,
  CommissionConfigRequest,
  CommissionOverrideRequest,
  SetZonePolygonRequest,
  Zone,
} from '../types/zone.types';
import { OperatorView } from '../types/operator.types';

@Injectable({ providedIn: 'root' })
export class ZonesService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/operator/api/zones`;

  /** All zones — non paginated. */
  list(): Observable<Zone[]> {
    return this.http.get<Zone[]>(this.base);
  }

  get(code: string): Observable<Zone> {
    return this.http.get<Zone>(`${this.base}/${code}`);
  }

  create(body: AdminCreateZoneRequest): Observable<Zone> {
    return this.http.post<Zone>(this.base, body);
  }

  /** Partial update — `code` is immutable. */
  update(code: string, body: AdminUpdateZoneRequest): Observable<Zone> {
    return this.http.patch<Zone>(`${this.base}/${code}`, body);
  }

  delete(code: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${code}`);
  }

  setPolygon(code: string, body: SetZonePolygonRequest): Observable<Zone> {
    return this.http.put<Zone>(`${this.base}/${code}/polygon`, body);
  }

  /** Debug: tests whether a (lat,lng) point is in the zone polygon. */
  contains(code: string, lat: number, lng: number): Observable<boolean> {
    const params = new HttpParams()
      .set('lat', String(lat))
      .set('lng', String(lng));
    return this.http.get<boolean>(`${this.base}/${code}/contains`, { params });
  }
}

@Injectable({ providedIn: 'root' })
export class CommissionService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/operator/api/admin`;

  /** Singleton global commission config. */
  getGlobal(): Observable<CommissionConfig> {
    return this.http.get<CommissionConfig>(`${this.base}/commission-config`);
  }

  setGlobal(body: CommissionConfigRequest): Observable<CommissionConfig> {
    return this.http.put<CommissionConfig>(`${this.base}/commission-config`, body);
  }

  /** Apply (or clear, with all-null) per-operator override. */
  setOperatorOverride(operatorId: number, body: CommissionOverrideRequest): Observable<OperatorView> {
    return this.http.put<OperatorView>(
      `${this.base}/operators/${operatorId}/commission-override`,
      body,
    );
  }

  /** Paginated history of all rate changes (global + overrides). */
  history(page = 1, size = 20): Observable<ApiPageResponse<CommissionConfigHistory>> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));
    return this.http.get<ApiPageResponse<CommissionConfigHistory>>(
      `${this.base}/commission-history`,
      { params },
    );
  }

  historyOfOperator(operatorId: number, page = 1, size = 20): Observable<ApiPageResponse<CommissionConfigHistory>> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));
    return this.http.get<ApiPageResponse<CommissionConfigHistory>>(
      `${this.base}/operators/${operatorId}/commission-history`,
      { params },
    );
  }
}
